import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import ToastMsg from "../../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../../Utility/API/Serviceurls";

const MAX_CALL_DURATION = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 13 * 60 * 1000;      // 13 minutes
const MAX_PARTICIPANTS = 2;   
let accessToken = localStorage.getItem("AccessToken");           // doctor + patient only

// Parse ISO-ish string safely
const parseIsoToMs = (val) => {
  if (!val || typeof val !== "string") return NaN;
  let ms = Date.parse(val);
  if (Number.isFinite(ms)) return ms;
  if (/^\d{4}-\d{2}-\d{2}T/.test(val) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(val)) {
    ms = Date.parse(val + "Z");
  }
  return ms;
};

// Fallback only (not used with your current API)
const parseHHmmOnServerDay = (hhmm, serverNowMs) => {
  if (!hhmm) return NaN;
  const [hh, mm] = String(hhmm).split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  const d = new Date(serverNowMs);
  d.setHours(hh, mm, 0, 0);
  return d.getTime();
};

const AgoraVideoCall = ({
  channelName = "",
  onClose,
  patientName = "Patient",
  doctorName = "Doctor",
  appoinID = "",
  onCallDurationUpdate,
  onWarningTrigger,
  userRole = "patient",
}) => {
  const clientRef = useRef(null);
  const localTracksRef = useRef({ mic: null, cam: null });

  const callTimerRef = useRef(null);
  const initStartedRef = useRef(false);
  const hasCalledEndCallRef = useRef(false); // Track if endCall API was already called

  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [warningShown, setWarningShown] = useState(false);

  const [isErrorMsg, setisErrorMsg] = useState(false);
  const [isErrorMsgText, setisErrorMsgText] = useState("");
  const [isSuccessMsg, setisSuccessMsg] = useState(false);
  const [isSuccessMsgText, setisSuccessMsgText] = useState("");

  const [remoteUserInfo, setRemoteUserInfo] = useState({});
  const [appID, setappID] = useState(null);
  const [token, settoken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAgoraReady, setIsAgoraReady] = useState(false);
  const [connectionState, setConnectionState] = useState("DISCONNECTED");

  const [apiStartTime, setApiStartTime] = useState(null);
  const [callEndTime, setCallEndTime] = useState(null);
  const [clockSkewMs, setClockSkewMs] = useState(0); // server_now - client_now

  
  useEffect(() => {
    if (channelName) getTeleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName]);

  const getTeleData = () => {
    try {
      RestAPIService.getAll(
        `${Serviceurls.TELE_GET_DATA}?channel_name=${encodeURIComponent(channelName)}`
      )
        .then((response) => {
          if (response?.data?.status !== "success") {
            errorMessage(
              "Failed to get call data: " + (response?.data?.message || "Unknown error")
            );
            return;
          }

          const teleData = response.data?.data;
          if (!teleData?.agora_app_id) {
            errorMessage("App ID not found in response");
            return;
          }

          setappID(teleData.agora_app_id);
          settoken(teleData?.agora_token || null);

          // Authoritative server "now"
          let serverNowMs = NaN;
          if (teleData?.current_datetime) {
            serverNowMs = parseIsoToMs(teleData.current_datetime);
          }
          if (!Number.isFinite(serverNowMs)) {
            const serverDateHeader = response?.headers?.date;
            const fromHeader = serverDateHeader ? Date.parse(serverDateHeader) : NaN;
            serverNowMs = Number.isFinite(fromHeader) ? fromHeader : Date.now();
          }

          // Clock skew correction
          const skew = serverNowMs - Date.now();
          setClockSkewMs(skew);

          // Start time
          let startMs = parseIsoToMs(teleData?.created_date);
          if (!Number.isFinite(startMs) && teleData?.created_date) {
            startMs = parseHHmmOnServerDay(teleData.created_date, serverNowMs);
          }

          if (!Number.isFinite(startMs)) {
            errorMessage("Unable to determine call start time from API");
            return;
          }

          const endMs = startMs + MAX_CALL_DURATION;
          setApiStartTime(startMs);
          setCallEndTime(endMs);

          startCallTimer(startMs, endMs);
          setIsInitialized(true);
        })
        .catch((error) => {
          errorMessage(
            error?.response?.data?.message || error?.message || "Failed to get call information"
          );
        });
    } catch (e) {
      errorMessage(e?.message || "Failed to initialize call");
    }
  };

  // --- TIMER ---
  const startCallTimer = (startTime, endTime) => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    callTimerRef.current = setInterval(() => {
      const now = Date.now() + clockSkewMs;
      if (now >= endTime) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
        errorMessage("Call duration limit reached (15 minutes)");
        setTimeout(() => {
          endCall();
        }, 1000);
        return;
      }
      const elapsed = Math.max(0, now - startTime);
      const elapsedSeconds = Math.floor(elapsed / 1000);
      onCallDurationUpdate?.(elapsedSeconds);

      if (!warningShown && elapsed >= WARNING_TIME) {
        setWarningShown(true);
        onWarningTrigger?.(true);
        // successMessage("Call will end in 2 minutes");
      }
    }, 1000);
  };

  const stopAllTimers = () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }
    onCallDurationUpdate?.(0);
    onWarningTrigger?.(false);
    setWarningShown(false);
  };

  // --- CLEANUP ---
  const cleanupAndLeave = async () => {
    const client = clientRef.current;
    const { mic, cam } = localTracksRef.current || {};
    try {
      if (mic) {
        mic.stop(); mic.close();
      }
      if (cam) {
        cam.stop(); cam.close();
      }
      if (client && client.connectionState !== "DISCONNECTED") {
        await client.leave();
      }
      localTracksRef.current = { mic: null, cam: null };
      setRemoteUsers([]);
      setRemoteUserInfo({});
      setConnectionState("DISCONNECTED");
      setIsAgoraReady(false);
      initStartedRef.current = false;
    } catch (e) {
      console.error("cleanup error:", e);
    }
  };

  // If we detect >2 total, reject locally
  const rejectRoomIsFull = async (reason = "Room is full (max 2 users).") => {
    errorMessage(reason);
    await cleanupAndLeave();
    onClose?.();
  };

  // --- UI HELPERS ---
  const getRemoteUserName = (user) =>
    remoteUserInfo[user.uid]?.name ?? (userRole === "doctor" ? patientName : doctorName);

  const getRemoteUserRole = (user) =>
    remoteUserInfo[user.uid]?.role ?? (userRole === "doctor" ? "patient" : "doctor");

  const getFirstLetter = (name) => (name ? name.charAt(0).toUpperCase() : "U");

  // --- TOGGLES ---
  const toggleMute = async () => {
    if (isProcessing) return;
    const mic = localTracksRef.current.mic;
    if (!mic) return errorMessage("Microphone not available");
    setIsProcessing(true);
    try {
      await mic.setEnabled(isMuted); // flip
      setIsMuted(!isMuted);
    } catch {
      errorMessage("Failed to toggle microphone");
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleVideo = async () => {
    if (isProcessing) return;
    const cam = localTracksRef.current.cam;
    if (!cam) return errorMessage("Camera not available");
    setIsProcessing(true);
    try {
      await cam.setEnabled(!isVideoOn ? true : false);
      const next = !isVideoOn;
      setIsVideoOn(next);
      if (next) {
        setTimeout(() => {
          try {
            const localPlayer = document.getElementById("local-player");
            if (localPlayer) cam.play("local-player");
          } catch (e) {
            console.error("Video play error:", e);
          }
        }, 100);
      }
    } catch {
      errorMessage("Failed to toggle camera");
    } finally {
      setIsProcessing(false);
    }
  };

  // --- AGORA BOOTSTRAP ---
  const checkAgoraRequirements = () => {
    if (!appID) return errorMessage("App ID is missing"), false;
    if (!channelName) return errorMessage("Channel name is missing"), false;
    if (!AgoraRTC.checkSystemRequirements())
      return errorMessage("Browser does not support WebRTC"), false;
    return true;
  };

  const initAgora = async () => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    if (!checkAgoraRequirements()) {
      initStartedRef.current = false;
      return;
    }

    try {
      const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      clientRef.current = client;
      setupAgoraEventListeners(client);

      // Join
      try {
        await client.join(appID, channelName, token, null);

        // Immediately enforce 2-user cap for the 3rd joiner
        // (local user counts as 1; remote users currently in channel are client.remoteUsers)
        console.log("Remote users on join:", client.remoteUsers);
        if (client.remoteUsers.length > MAX_PARTICIPANTS) {
          await rejectRoomIsFull();
          return;
        }

        await createAndPublishTracks(client);
      } catch (joinError) {
        handleJoinError(joinError);
        initStartedRef.current = false;
        return;
      }
    } catch (error) {
      errorMessage("Failed to initialize video call. Please check your connection and try again.");
      initStartedRef.current = false;
    }
  };

  const setupAgoraEventListeners = (client) => {
    client.on("connection-state-change", (cur, prev) => {
      setConnectionState(cur);
      if (cur === "CONNECTED") successMessage("Connected to call");
      if (cur === "RECONNECTING") errorMessage("Reconnecting to call...");
      if (cur === "DISCONNECTED" && prev !== "DISCONNECTING") errorMessage("Disconnected from call");
    });

    // If a new user publishes but would exceed cap, reject (leave)
    client.on("user-published", async (user, mediaType) => {
      // If we already have 1 remote, this new publish would make it 2 remotes (total 3) — reject.
      const currentRemoteCount = client.remoteUsers.length;
      const willBeTotal = 1 /* me */ + currentRemoteCount; // before subscribing
      if (willBeTotal > MAX_PARTICIPANTS) {
        await rejectRoomIsFull();
        return;
      }

      try {
        await client.subscribe(user, mediaType);
        await handleRemoteUserJoin(client, user, mediaType);
      } catch (e) {
        console.error("user-published handler error:", e);
      }
    });

    client.on("user-unpublished", (user, mediaType) => {
      if (mediaType === "video") {
        setRemoteUsers((prev) =>
          prev.map((u) => (u.uid === user.uid ? { ...u, hasVideo: false } : u))
        );
      } else if (mediaType === "audio") {
        setRemoteUsers((prev) =>
          prev.map((u) => (u.uid === user.uid ? { ...u, hasAudio: false } : u))
        );
      }
    });
    console.log(window.location.pathname, '>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>')

    client.on("user-left", (user) => {
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
      setRemoteUserInfo((prev) => {
        const next = { ...prev };
        delete next[user.uid];
        return next;
      });
      const leftUserRole = remoteUserInfo[user.uid]?.role ||
        (userRole === "doctor" ? "patient" : "doctor");

      const leftUserName = remoteUserInfo[user.uid]?.name ||
        (userRole === "doctor" ? patientName : doctorName);

      successMessage(`${leftUserName} left the call.`);
      if (leftUserRole === "doctor" && userRole === "patient") {
        // Doctor left - automatically end call for patient after short delay
        setTimeout(() => {
          successMessage("Doctor has ended the call.");
          leaveCall();
        }, 1500);
      }
      
    });
  };

  const handleJoinError = (err) => {
    let msg = "Failed to join call: ";
    if (err.code === "INVALID_APP_ID") msg = "Invalid App ID.";
    else if (err.code === "INVALID_CHANNEL_NAME") msg = "Invalid channel name.";
    else if (err.code === "DYNAMIC_KEY_EXPIRED") msg = "Token expired.";
    else if (err.code === "CAN_NOT_GET_GATEWAY_SERVER") msg = "Network connection failed.";
    else if (err.code === "JOIN_CHANNEL_REJECTED")
      msg = "Join rejected. Channel may be full or you may not have permission.";
    else if (err.message?.includes("TOO_MANY_USERS"))
      msg = "Maximum users limit reached.";
    else msg += err.message || "Unknown error";
    errorMessage(msg);
  };

  const createAndPublishTracks = async (client) => {
    try {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      } catch {
        errorMessage("Camera/microphone permissions denied. Please allow permissions and refresh.");
        await client.leave();
        return;
      }

      const [micTrack, camTrack] = await Promise.all([
        AgoraRTC.createMicrophoneAudioTrack().catch(() => null),
        AgoraRTC.createCameraVideoTrack({
          encoderConfig: "480p_1",
          optimizationMode: "motion",
        }).catch(() => null),
      ]);

      if (!micTrack || !camTrack) {
        errorMessage("Failed to access media devices.");
        await client.leave();
        return;
      }

      localTracksRef.current = { mic: micTrack, cam: camTrack };

      await micTrack.setEnabled(!isMuted);
      await camTrack.setEnabled(isVideoOn);

      setTimeout(() => {
        try {
          const localPlayer = document.getElementById("local-player");
          if (localPlayer) camTrack.play("local-player");
        } catch (e) {
          console.error("Local video play error:", e);
        }
      }, 250);

      await client.publish([micTrack, camTrack]);
      setIsAgoraReady(true);

      // Subscribe to already-present remote (if any)
      client.remoteUsers.forEach(async (user) => {
        // Enforce cap again defensively
        if (1 /* me */ + client.remoteUsers.length > MAX_PARTICIPANTS) {
          await rejectRoomIsFull();
          return;
        }
        await handleRemoteUserJoin(client, user, null);
      });
    } catch (e) {
      errorMessage("Failed to setup media devices.");
      try { await client.leave(); } catch {}
    }
  };

  const handleRemoteUserJoin = async (client, user, mediaType = null) => {
    // Update roster (we only ever render 1 remote tile for layout)
    setRemoteUserInfo((prev) => ({
      ...prev,
      [user.uid]: {
        name: userRole === "doctor" ? patientName : doctorName,
        role: userRole === "doctor" ? "patient" : "doctor",
      },
    }));

    setRemoteUsers((prev) => {
      const exists = prev.find((u) => u.uid === user.uid);
      if (!exists) return [...prev, { ...user, hasVideo: true, hasAudio: true }];
      return prev.map((u) => (u.uid === user.uid ? { ...u, hasVideo: true, hasAudio: true } : u));
    });

    // Play tracks
    if (mediaType === "video" || mediaType === null) {
      try {
        await client.subscribe(user, "video");
      } catch {}
    }
    if (mediaType === "audio" || mediaType === null) {
      try {
        await client.subscribe(user, "audio");
      } catch {}
    }

    setTimeout(() => {
      const container = document.getElementById(`remote-player-${user.uid}`);
      if (container && user.videoTrack) {
        try {
          user.videoTrack.play(container);
        } catch (e) {
          console.error("Remote video play error:", e);
        }
      }
    }, 300);

    if (user.audioTrack) {
      try { user.audioTrack.play(); } catch (e) { console.error("Remote audio error:", e); }
    }
  };

  // Initialize after tele data ready
  useEffect(() => {
    if (isInitialized && appID && channelName) initAgora();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, appID, channelName]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllTimers();
      cleanupAndLeave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- MESSAGES & CONTROLS ---
  const errorMessage = (msg) => {
    setisErrorMsg(true);
    setisErrorMsgText(msg);
  };
  const successMessage = (msg) => {
    setisSuccessMsg(true);
    setisSuccessMsgText(msg);
  };
  const msgClose = () => {
    setisErrorMsg(false);
    setisErrorMsgText("");
    setisSuccessMsg(false);
    setisSuccessMsgText("");
  };

  const leaveCall = async () => {
    try {
      setIsProcessing(true);
      stopAllTimers();
      await cleanupAndLeave();

      if (userRole === "patient") {
        onClose?.();
      } else {
        const response = await RestAPIService.getAllForTeleEndCall(
          `${Serviceurls.TELE_END_CALL}?appointment_id=${encodeURIComponent(
            appoinID
          )}&channel_name=${encodeURIComponent(channelName)}`, accessToken
        );
        if (response?.data?.status === "success") onClose?.();
      }
    } catch (e) {
      errorMessage(e?.response?.data?.message || e?.message || "Failed to leave call");
    } finally {
      setIsProcessing(false);
    }
  };

  const endCall = async () => {
    try {
      setIsProcessing(true);
      stopAllTimers();
      hasCalledEndCallRef.current = true; // Mark as called
      
      const response = await RestAPIService.getAllForTeleEndCall(
        `${Serviceurls.TELE_END_CALL}?appointment_id=${encodeURIComponent(appoinID)}&channel_name=${encodeURIComponent(channelName)}`,
        accessToken
      );

      if (response?.data?.status === "success") {
        await cleanupAndLeave();
        onClose?.();
      }
    } catch (e) {
      errorMessage(e?.response?.data?.message || e?.message || "Failed to end call");
    } finally {
      setIsProcessing(false);
    }
  };

  const retryInitialization = () => {
    stopAllTimers();
    setIsInitialized(false);
    setisErrorMsg(false);
    getTeleData();
  };

  // --- RESPONSIVE LAYOUT (2 users) ---
  // We only render at most 1 remote tile visually.
  const visibleRemotes = remoteUsers.slice(0, 1);
  const twoUp = visibleRemotes.length === 1;

  return (
    <div style={styles.wrapper}>
      {isErrorMsg && isErrorMsgText.includes("Failed to initialize") && (
        <div style={styles.retryContainer}>
          <div style={styles.retryMessage}>{isErrorMsgText}</div>
          <button style={styles.retryButton} onClick={retryInitialization}>
            🔄 Retry Connection
          </button>
        </div>
      )}

      {/* Connection Status + End-time */}
      <div
        style={{
          ...styles.connectionStatus,
          background:
            connectionState === "CONNECTED"
              ? "#4CAF50"
              : connectionState === "CONNECTING" || connectionState === "RECONNECTING"
              ? "#FF9800"
              : "#f44336",
        }}
      >
        Status:{" "}
        {connectionState === "CONNECTED"
          ? "Connected"
          : connectionState === "CONNECTING"
          ? "Connecting..."
          : connectionState === "RECONNECTING"
          ? "Reconnecting..."
          : "Disconnected"}
        {apiStartTime && (
          <span style={{ marginLeft: 10 }}>
            | Call ends at: {callEndTime ? new Date(callEndTime).toLocaleTimeString() : "…"}
          </span>
        )}
      </div>

      <div
        id="remote-container"
        style={{
          ...styles.videoContainer,
          flexDirection: twoUp ? "row" : "row",
          flexWrap: "nowrap",
          gap: "10px",
        }}
      >
        {/* LOCAL TILE */}
        <div
          id="local-player"
          style={{
            ...styles.tile,
            width: twoUp ? "49%" : "100%",
            height: "100%",
            background: isVideoOn ? "#000" : "#333",
          }}
        >
          {isMuted && (
            <div style={styles.muteIndicator}>
              <div style={styles.muteIcon}>🔇</div>
              <div style={styles.muteText}>Muted</div>
            </div>
          )}
          {!isVideoOn && (
            <div style={styles.avatarContainer}>
              <div style={styles.avatarCircle}>
                <span style={styles.avatarText}>
                  {(userRole === "doctor" ? doctorName : patientName).charAt(0).toUpperCase()}
                </span>
              </div>
              <div style={styles.userName}>
                {userRole === "doctor" ? doctorName : patientName} (You)
              </div>
              {isMuted && <div style={styles.avatarMuteIndicator}>🔇 Muted</div>}
            </div>
          )}
        </div>

        {/* REMOTE TILE (max 1 shown) */}
        {visibleRemotes.map((user) => {
          const remoteName = getRemoteUserName(user);
          const remoteRole = getRemoteUserRole(user);
          return (
            <div
              key={user.uid}
              id={`remote-player-${user.uid}`}
              style={{
                ...styles.tile,
                width: twoUp ? "49%" : "48%",
                height: "100%",
                background: user.hasVideo ? "#000" : "#333",
              }}
            >
              {!user.hasAudio && (
                <div style={styles.muteIndicator}>
                  <div style={styles.muteIcon}>🔇</div>
                  <div style={styles.muteText}>Muted</div>
                </div>
              )}
              {!user.hasVideo && (
                <div style={styles.avatarContainer}>
                  <div style={styles.avatarCircle}>
                    <span style={styles.avatarText}>{remoteName.charAt(0).toUpperCase()}</span>
                  </div>
                  <div style={styles.userName}>
                    {remoteName} ({remoteRole})
                  </div>
                  {!user.hasAudio && <div style={styles.avatarMuteIndicator}>🔇 Muted</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.controls}>
        <button
          style={{ ...styles.btn, opacity: isProcessing ? 0.6 : 1 }}
          onClick={toggleMute}
          disabled={isProcessing}
        >
          {isMuted ? "🔇 Unmute" : "🎤 Mute"}
        </button>
        <button
          style={{ ...styles.btn, opacity: isProcessing ? 0.6 : 1 }}
          onClick={toggleVideo}
          disabled={isProcessing}
        >
          {isVideoOn ? "📹 Video Off" : "📹 Video On"}
        </button>
        <button
          style={{
            ...styles.btn,
            background: "red",
            color: "#fff",
            opacity: isProcessing ? 0.6 : 1,
          }}
          onClick={userRole === "doctor" ? endCall : leaveCall}
          disabled={isProcessing}
        >
          {userRole === "doctor" ? "📞 End Call" : "📞 Leave Call"}
        </button>
      </div>

      {isErrorMsg && <ToastMsg severity={"error"} msg={isErrorMsgText} msgPop={msgClose} />}
      {isSuccessMsg && <ToastMsg severity={"success"} msg={isSuccessMsgText} msgPop={msgClose} />}
    </div>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    height: "75vh",
    background: "#1a1a1a",
    color: "#fff",
  },
  connectionStatus: {
    padding: "8px 16px",
    color: "#fff",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "bold",
  },
  retryContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    background: "#2d2d2d",
    gap: "15px",
  },
  retryMessage: {
    color: "#ff6b6b",
    textAlign: "center",
    fontSize: "16px",
  },
  retryButton: {
    padding: "10px 20px",
    background: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
  },
  videoContainer: {
    flex: 1,
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    padding: "10px",
    gap: "10px",
    overflow: "hidden",
  },
  tile: {
    border: "2px solid #333",
    borderRadius: "8px",
    position: "relative",
    overflow: "hidden",
    minWidth: "300px",
    minHeight: "220px",
  },
  controls: {
    display: "flex",
    justifyContent: "center",
    padding: "6px",
    background: "#222",
    gap: "15px",
    alignItems: "center",
    height: '7vh'
  },
  btn: {
    padding: "12px 24px",
    border: "none",
    borderRadius: "8px",
    background: "#444",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s",
    minWidth: "120px",
    marginTop: '0%'
  },
  muteIndicator: {
    position: "absolute",
    top: "10px",
    left: "10px",
    background: "rgba(0, 0, 0, 0.7)",
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    zIndex: 10,
  },
  muteIcon: { fontSize: "16px" },
  muteText: { fontSize: "12px", color: "#fff" },
  avatarContainer: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#333",
    zIndex: 10,
  },
  avatarCircle: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    background: "#007bff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
    border: "2px solid #fff",
  },
  avatarText: { fontSize: "32px", color: "#fff", fontWeight: "bold" },
  userName: { fontSize: "16px", color: "#fff", fontWeight: "500", marginBottom: "5px", textAlign: "center" },
  avatarMuteIndicator: {
    background: "rgba(0, 0, 0, 0.7)",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "11px",
  },
};

export default AgoraVideoCall;